import dayjs from 'dayjs'
import {
  CSSProperties,
  ReactNode,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'

import { formatMoney } from '@percent/utility'
import {
  Alert,
  Badge,
  Box,
  Checkbox,
  Flex,
  Icon,
  Select,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip,
  Typography,
} from '@percent/lemonade'

import { useFilters } from '../../hooks/useFilters'
import {
  DatasetSort,
  DEFAULT_PAGE_SIZE,
  useInsightsDataset,
} from '../../hooks/useInsightsDataset'
import {
  DatasetCellValue,
  DatasetColumn,
  DatasetColumnDataType,
  DatasetColumnValue,
  DatasetRow,
  DynamicColumnsOption,
  isDurationValue,
  isMoneyValue,
} from '../../types/insights.types'
import { formatCount, formatDuration } from '../../utils/formatValue'

// The dataset table is framed as a card per the design (Figma border/default-lighter
// 2px outline, 16px radius) with border/default row dividers. The base lemonade
// TableWrapper is borderless with an 8px radius, so we extend it here — scoped to
// insights datasets rather than changing the shared component. `className` lands on
// the TableWrapper, so the descendant rules retint the dividers, which sit on the
// header cells (th) and the row elements (tr).
const BorderedTableProvider = styled(TableProvider)`
  ${({ theme }) => `
    border: 2px solid ${theme.colors.primarySurface};
    border-radius: ${theme.borderRadii.xlarge};

    th,
    tr {
      border-bottom-color: ${theme.colors.gray90};
    }
  `}
`

// The card header sits inside the bordered card, above the table. It mirrors the
// StatCard treatment used by the metric cards (label + optional info-icon tooltip
// on the left, actions pinned right) so the dataset card reads as one of the same
// family of cards on the report.
const DatasetHeader = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.sizes.xs};
  `}
`

const HeaderLabelGroup = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.sizes.xxs};
    min-width: 0;
  `}
`

// Same type ramp as StatCard's `Label` (bodySmall / medium / content-tertiary) so
// the dataset title matches the metric card titles exactly.
const HeaderLabel = styled.span`
  ${({ theme }) => `
    font-size: ${theme.fontSizes.bodySmall};
    font-weight: ${theme.fontWeights.medium};
    line-height: 20px;
    color: ${theme.colors.contentTertiary};
  `}
`

// Unstyled, keyboard-focusable trigger so the description tooltip is reachable
// without a mouse — matches StatCard's info button.
const HeaderInfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: help;
  line-height: 0;
`

// Dynamic columns are headed by full form-question labels; one long label would
// otherwise wrap down the narrow column and stretch the whole header row.
const HeaderText = styled.span`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  overflow-wrap: anywhere;
`

function ColumnHeader({ text }: Readonly<{ text: string }>) {
  const ref = useRef<HTMLSpanElement>(null)
  const [isClamped, setIsClamped] = useState(false)

  useLayoutEffect(() => {
    const element = ref.current
    if (element) setIsClamped(element.scrollHeight > element.clientHeight)
  }, [text])

  return (
    <Tooltip content={isClamped ? text : undefined}>
      <HeaderText ref={ref}>{text}</HeaderText>
    </Tooltip>
  )
}

const EMPTY_CELL = '—'

const toValueLabels = (
  values?: DatasetColumnValue[]
): Record<string, string> | undefined =>
  values && values.length > 0
    ? Object.fromEntries(values.map(({ value, label }) => [value, label]))
    : undefined

/**
 * How the app tweaks a column. The header label, data type, sortability and
 * value→label map all come from the self-describing response; the config only
 * selects/orders columns and layers minimal overrides on top.
 */
export type DatasetColumnConfig = {
  /** Column key (matches a response column + row key). */
  id: string
  /** Pins the column on horizontal scroll (e.g. an identifier column). */
  isSticky?: boolean
  width?: string
  /** Override the response's header label. */
  header?: string
  /** Override the response's label for specific raw values (e.g. `{ hosted: '…' }`). */
  valueLabelOverrides?: Record<string, string>
  /** Full custom cell renderer; overrides the dataType/value-label rendering. */
  render?: (value: DatasetCellValue, row: DatasetRow) => ReactNode
}

export type InsightsDatasetLabels = {
  /** Empty-state heading when there are no rows. */
  empty: string
  emptyDescription?: string
  /** Footer results count, e.g. `count => t('table.results', { count })`. */
  results: (count: number) => string
  /** Shown in place of `empty` when the request errored (vs. genuinely no rows). */
  error: string
}

/**
 * Footer page-size selector config. `options` + `label` are co-required (bundled
 * here so you can't supply one without the other); omit the whole prop for a
 * fixed page size with no selector.
 */
export type DatasetPageSizeConfig = {
  /** At least one option (the first is the default selection). */
  options: [number, ...number[]]
  label: string
  /** Initial selection; defaults to the first option. */
  default?: number
}

export type InsightsDatasetProps = {
  /** Dataset slug (e.g. `donations`). */
  dataset: string
  /**
   * Card title, rendered inside the card in the metric-card title style. Omit for
   * a card with no header (unless an `action` is supplied).
   */
  title?: string
  /**
   * Explanatory text shown via an info icon + tooltip beside the title — the same
   * treatment the metric cards use. The icon only renders when this is provided.
   */
  description?: string
  /**
   * Card-header actions, pinned to the right of the title (e.g. an
   * `<Insights.Export>` CSV button). Rendered inside the card even without a title.
   */
  action?: ReactNode
  /** Columns to show + order, with optional overrides. Omit to render every response column. */
  columns?: DatasetColumnConfig[]
  /** Offers a checkbox that appends the dataset's dynamic columns after `columns`. */
  dynamicColumns?: DynamicColumnsOption
  /** Fixed page size when no selector is shown. */
  pageSize?: number
  /** Renders a page-size selector in the footer (options + label co-required). */
  pageSizeSelector?: DatasetPageSizeConfig
  defaultSort?: DatasetSort
  labels: InsightsDatasetLabels
  testId?: string
}

// A column resolved from the response metadata merged with the caller's config.
type ResolvedColumn = {
  id: string
  header: string
  dataType: DatasetColumnDataType
  isSortable?: boolean
  isSticky?: boolean
  width?: string
  valueLabels?: Record<string, string>
  render?: (value: DatasetCellValue, row: DatasetRow) => ReactNode
}

// Columns get a stable width so the layout doesn't reflow as each page's content
// changes (the table is auto-layout + max-content, so without this the widest
// value on a page sizes the column). Width comes from the column config, else a
// per-type default; cell content is clipped to it (see `clampStyle`).
const DEFAULT_COLUMN_WIDTHS: Record<DatasetColumnDataType, string> = {
  money: '140px',
  duration: '140px',
  datetime: '200px',
  string: '220px',
  stringArray: '240px',
}

const columnWidth = (column: ResolvedColumn): string =>
  column.width ?? DEFAULT_COLUMN_WIDTHS[column.dataType] ?? '220px'

// A fixed-width, single-line clip with an ellipsis. Applied to the cell's content
// box (not the <td>): in auto-layout a long unbroken value would otherwise set the
// column's min-content width and stretch it, so bounding the content box is what
// actually pins the column.
const clampStyle = (width: string): CSSProperties => ({
  width,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

// Money and durations read as magnitudes, so those columns are right-aligned the
// way currency is conventionally tabulated — they line up on the right and are
// easier to scan/compare down the column. Header and values share this one style
// so they can't drift apart. Driven off the response dataType so every such column
// across every dataset gets it without per-report config.
const isRightAligned = (dataType: DatasetColumnDataType): boolean =>
  dataType === 'money' || dataType === 'duration'

const alignStyle = (column: ResolvedColumn): CSSProperties | undefined =>
  isRightAligned(column.dataType) ? { textAlign: 'right' } : undefined

// Default cell rendering by data type; money shows decimals (full precision), a
// duration reads in its own unit, a datetime shows the time too, and a string array
// renders as pills. A cell whose value doesn't match its declared type renders empty
// rather than guessing.
function renderCell(
  column: ResolvedColumn,
  value: DatasetCellValue,
  row: DatasetRow
): ReactNode {
  if (column.render) return column.render(value, row)

  switch (column.dataType) {
    case 'money':
      return isMoneyValue(value)
        ? formatMoney({ amount: value.amount, currency: value.currency })
        : EMPTY_CELL
    case 'duration':
      return isDurationValue(value)
        ? formatDuration(value.value, value.unit)
        : EMPTY_CELL
    case 'datetime':
      return typeof value === 'string' && dayjs(value).isValid()
        ? dayjs(value).format('D MMM YYYY, HH:mm')
        : EMPTY_CELL
    case 'stringArray':
      return Array.isArray(value) && value.length > 0 ? (
        <Flex gap="xs" wrap="wrap">
          {value.map((item, index) => (
            <Badge key={`${index}-${item}`} variant="default">
              {item}
            </Badge>
          ))}
        </Flex>
      ) : (
        EMPTY_CELL
      )
    default:
      if (typeof value === 'string' && value.length > 0)
        return column.valueLabels?.[value] ?? value
      // The untagged `ValueType` case — a plain count, grouped like every other
      // number in the library rather than falling through as empty.
      if (typeof value === 'number') return formatCount(value)

      return EMPTY_CELL
  }
}

// Merge the self-describing response columns with the caller's config: the config
// drives projection + order + overrides, the response drives label/type/sortable.
function resolveColumns(
  responseColumns: DatasetColumn[],
  configs: DatasetColumnConfig[] | undefined
): ResolvedColumn[] {
  const byKey = new Map(responseColumns.map((column) => [column.key, column]))

  if (configs && configs.length > 0) {
    const dynamicConfigs = responseColumns
      .filter((column) => column.group)
      .map((column): DatasetColumnConfig => ({ id: column.key }))

    return [...configs, ...dynamicConfigs].map((config) => {
      const column = byKey.get(config.id)

      return {
        id: config.id,
        header: config.header ?? column?.label ?? config.id,
        dataType: column?.dataType ?? 'string',
        isSortable: column?.sortable,
        isSticky: config.isSticky,
        width: config.width,
        valueLabels: {
          ...toValueLabels(column?.values),
          ...config.valueLabelOverrides,
        },
        render: config.render,
      }
    })
  }

  return responseColumns.map((column) => ({
    id: column.key,
    header: column.label,
    dataType: column.dataType,
    isSortable: column.sortable,
    valueLabels: toValueLabels(column.values),
  }))
}

/**
 * A paginated, sortable, filterable dataset table. Reads the active filter values
 * from the surrounding `<Insights.Domain>` (plus its pinned filters), fetches one
 * page via `getDataset`, and renders rows from the response's self-describing
 * column metadata — the caller's `columns` config only selects/orders columns and
 * layers minimal overrides. Sortable headers re-query (resetting to the first
 * page); pagination follows the response `_links`.
 */
export function InsightsDataset({
  dataset,
  title,
  description,
  action,
  columns: columnConfigs,
  dynamicColumns,
  pageSize: fixedPageSize,
  pageSizeSelector,
  defaultSort,
  labels,
  testId,
}: Readonly<InsightsDatasetProps>) {
  const { values: filters } = useFilters()
  const [sort, setSort] = useState<DatasetSort | undefined>(defaultSort)
  const [pageSize, setPageSize] = useState(
    pageSizeSelector?.default ??
      pageSizeSelector?.options[0] ??
      fixedPageSize ??
      DEFAULT_PAGE_SIZE
  )

  // Stable projection (request only the configured columns) so the query key
  // doesn't churn across renders.
  const columnIds = useMemo(
    () => columnConfigs?.map((config) => config.id),
    [columnConfigs]
  )

  const [dynamicColumnsTicked, setDynamicColumnsTicked] = useState(false)
  const dynamicColumnsAvailable = dynamicColumns?.isAvailable(filters) ?? false
  const dynamicColumnsGroup = dynamicColumns?.group
  const columnGroups = useMemo(
    () =>
      dynamicColumnsGroup && dynamicColumnsTicked && dynamicColumnsAvailable
        ? [dynamicColumnsGroup]
        : undefined,
    [dynamicColumnsGroup, dynamicColumnsTicked, dynamicColumnsAvailable]
  )

  const {
    columns: responseColumns,
    rows,
    totalResults,
    isLoading,
    error,
    nextPage,
    previousPage,
  } = useInsightsDataset(dataset, {
    filters,
    sort,
    pageSize,
    columns: columnIds,
    columnGroups,
  })

  const resolved = useMemo(
    () => resolveColumns(responseColumns, columnConfigs),
    [responseColumns, columnConfigs]
  )

  // A fresh column starts DESC (largest / most-recent first — the default these
  // donation tables want); clicking the active column toggles its direction.
  const handleRequestSort = (columnKey: string) =>
    setSort((prev) =>
      prev?.column === columnKey
        ? {
            column: columnKey,
            direction: prev.direction === 'ASC' ? 'DESC' : 'ASC',
          }
        : { column: columnKey, direction: 'DESC' }
    )

  const tableColumns: TableColumn[] = resolved.map((column) => {
    const width = columnWidth(column)

    // minWidth feeds the sticky-offset calc in useColumnStyles; width/maxWidth pin
    // the header cell so it lines up with the clamped body cells below.
    return {
      id: column.id,
      content: <ColumnHeader text={column.header} />,
      isSortable: column.isSortable,
      isSticky: column.isSticky,
      props: {
        width,
        minWidth: width,
        maxWidth: width,
        ...alignStyle(column),
      },
    }
  })

  // On error the table also has no rows, so the empty-state slot doubles as the
  // error slot — show the error copy there, distinct from genuinely-no-rows.
  const emptyTitle = error ? labels.error : labels.empty

  const pageSizeControl = pageSizeSelector ? (
    <Flex direction="row" align="center" gap={8}>
      <Typography>{pageSizeSelector.label}</Typography>
      <Box style={{ width: '100px' }}>
        <Select
          name="dataset-page-size"
          options={pageSizeSelector.options.map((size) => ({
            label: String(size),
            value: String(size),
          }))}
          defaultValue={{ label: String(pageSize), value: String(pageSize) }}
          onChange={(option) =>
            option?.value && setPageSize(Number(option.value))
          }
          orientation="up"
        />
      </Box>
    </Flex>
  ) : undefined

  return (
    <BorderedTableProvider
      columns={tableColumns}
      totalResults={totalResults}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={emptyTitle}
      emptyTableDescription={labels.emptyDescription}
      sortByColumn={sort?.column}
      sortDirection={sort?.direction}
      handleRequestSort={handleRequestSort}
      dataTestId={testId}
    >
      {(title || action || dynamicColumns) && (
        <DatasetHeader data-testid={testId ? `${testId}-header` : undefined}>
          <HeaderLabelGroup>
            {title && <HeaderLabel>{title}</HeaderLabel>}
            {description && (
              <Tooltip content={description}>
                <HeaderInfoButton
                  type="button"
                  aria-label={description}
                  data-testid={testId ? `${testId}-info` : undefined}
                >
                  <Icon name="info" size={4} color="gray400" />
                </HeaderInfoButton>
              </Tooltip>
            )}
          </HeaderLabelGroup>
          <Flex direction="row" align="center" gap={16}>
            {dynamicColumns && (
              <Checkbox
                name={`insights-dataset-${dataset}-dynamic-columns`}
                label={dynamicColumns.label}
                variant="default"
                active={dynamicColumnsTicked}
                onChange={() => setDynamicColumnsTicked((prev) => !prev)}
                dataTestId={testId ? `${testId}-dynamic-columns` : undefined}
              />
            )}
            {action}
          </Flex>
        </DatasetHeader>
      )}
      {dynamicColumns && dynamicColumnsTicked && !dynamicColumnsAvailable && (
        <Alert variant="error">{dynamicColumns.unavailable}</Alert>
      )}
      <Table>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              {resolved.map((column) => {
                const width = columnWidth(column)
                const value = row[column.id]
                const content = renderCell(column, value, row)

                // Pills wrap within the column; everything else is a single-line
                // ellipsis. Either way the content box is pinned to the column width.
                return (
                  <TableCell key={column.id}>
                    {column.dataType === 'stringArray' ? (
                      <Box style={{ width }}>{content}</Box>
                    ) : (
                      <Box
                        style={{ ...clampStyle(width), ...alignStyle(column) }}
                        title={typeof value === 'string' ? value : undefined}
                      >
                        {content}
                      </Box>
                    )}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        totalResultsText={labels.results(totalResults)}
        previousPage={previousPage}
        nextPage={nextPage}
        rightContent={pageSizeControl}
      />
    </BorderedTableProvider>
  )
}
